import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Hooks
import { useAppSelector } from "src/App/hooks";
// Common components
import { Button, TextInput, FullScreenLoading } from "src/Components";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
// Requests
import {
  setRollConfig,
  getRollConfig,
  getEmergencyRollConfig,
  setEmergencyRollConfig,
  getUpRollConfig,
  getDownRollConfig,
  setDownRollConfig,
  setUpRollConfig,
} from "src/Requests/coveredCall";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import type {
  IBaseInstrument,
  IRollConfigDto,
} from "src/Interfaces/coveredCall";

interface IProps {
  position: "up" | "down" | "emergency";
  onClose: () => void;
}
const SettingModal: React.FC<IProps> = ({ position, onClose }) => {
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // local store
  const [loading, setLoading] = React.useState(false);
  const [riskProfileId, setRiskProfileIds] = React.useState<string>(
    riskProfiles[0]?.id?.toString() ?? 0,
  );
  const [config, setConfig] = React.useState({
    baseInstruments: [] as IBaseInstrument[],
  } as IRollConfigDto);

  const getProperFunction = () => {
    switch (position) {
      case "emergency":
        return getEmergencyRollConfig(riskProfileId);
      case "up":
        return getUpRollConfig(riskProfileId);
      case "down":
        return getDownRollConfig(riskProfileId);
    }
  };

  const setProperFunction = () => {
    switch (position) {
      case "emergency":
        return setEmergencyRollConfig(riskProfileId, config);
      case "up":
        return setUpRollConfig(riskProfileId, config);
      case "down":
        return setDownRollConfig(riskProfileId, config);
    }
  };

  React.useEffect(() => {
    getProperFunction()
      .then((resp) => {
        setConfig(resp);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [riskProfileId]);

  const setConfigHandler = () => {
    setLoading(true);
    setProperFunction()
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const instrumentChangeHandler = (
    insId: string,
    rollType: "sourceRoll" | "destinationRoll" | "rollSpecification",
    name: string,
    value: any,
  ) => {
    setConfig((prevConfig) => {
      const clone = [...prevConfig.baseInstruments];
      const targetIndex = clone.findIndex((f) => f.instrumentId === insId);
      if (targetIndex === -1) return prevConfig;

      const instrument = clone[targetIndex];
      clone[targetIndex] = {
        ...instrument,
        [rollType]: {
          ...instrument[rollType],
          [name]: value,
        },
      };

      return { ...prevConfig, baseInstruments: clone };
    });
  };
  return (
    <div className="w-full flex flex-col justify-start items-center gap-5">
      {loading ? <FullScreenLoading /> : <></>}
      <SelectableButtons
        label="ریسک پروفایل"
        name="riskProfileId"
        onChange={(_: string, value: string) => setRiskProfileIds(value)}
        options={riskProfiles}
        value={riskProfileId}
        hideSelectAll
      />
      <div
        className="w-full grid gap-x-2 gap-y-2 text-xs text-center items-center"
        style={{
          gridTemplateColumns: `40px ${
            position !== "up" ? "1fr 1fr 1fr" : "2fr"
          } 32px 1fr 1fr 1fr 32px 1fr 1fr 1fr 1fr`,
        }}
      >
        <div />
        <div
          className="font-bold bg-sky-100 rounded py-1"
          style={{ gridColumn: `span ${position !== "up" ? 3 : 1}` }}
        >
          مبدا رول
        </div>
        <div />
        <div
          className="font-bold bg-sky-100 rounded py-1"
          style={{ gridColumn: "span 3" }}
        >
          مقصد رول
        </div>
        <div />
        <div
          className="font-bold bg-sky-100 rounded py-1"
          style={{ gridColumn: "span 4" }}
        >
          مشخصات رول
        </div>

        <div />
        {position !== "up" ? (
          <>
            <div>مرز DTM</div>
            <div>حداکثر ٪ ITM در کمتر از مرز</div>
            <div>حداکثر دلتا در بیشتر از مرز</div>
          </>
        ) : (
          <div>حداکثر ٪ بازده نگهداری روزانه</div>
        )}
        <div />
        <div>مرز DTM</div>
        <div>حداقل ٪ ITM در کمتر از مرز</div>
        <div>حداقل دلتا در بیشتر از مرز</div>
        <div />
        <div>حداقل ٪ بازده روزانه رول</div>
        <div>حداقل ٪ بازده روزانه خالص ارزش زمانی رول</div>
        <div>حداقل ٪ بهبود سر به سری</div>
        <div>حداقل بهبود دلتا</div>

        {config && config.baseInstruments && config.baseInstruments.length ? (
          config.baseInstruments.map((ins) => (
            <>
              <div>{ins.instrumentName}</div>
              {position !== "up" ? (
                <>
                  <TextInput
                    label="مرز DTM"
                    name="dtmPoint"
                    value={ins?.sourceRoll?.dtmPoint?.toString()}
                    onChange={(...props) =>
                      instrumentChangeHandler(
                        ins.instrumentId,
                        "sourceRoll",
                        ...props,
                      )
                    }
                  />
                  <TextInput
                    label="حداکثر ٪ ITM در کمتر از مرز"
                    name="maxITMPercentage_OverDTM"
                    value={ins?.sourceRoll?.maxITMPercentage_OverDTM?.toString()}
                    onChange={(...props) =>
                      instrumentChangeHandler(
                        ins.instrumentId,
                        "sourceRoll",
                        ...props,
                      )
                    }
                  />
                  <TextInput
                    label="حداکثر دلتا در بیشتر از مرز"
                    name="maxDeltaPercentage_UnderDTM"
                    value={ins?.sourceRoll?.maxDeltaPercentage_UnderDTM?.toString()}
                    onChange={(...props) =>
                      instrumentChangeHandler(
                        ins.instrumentId,
                        "sourceRoll",
                        ...props,
                      )
                    }
                  />
                </>
              ) : (
                <TextInput
                  label="حداکثر ٪ بازده نگهداری روزانه"
                  name="maxProfitPercentage"
                  value={ins?.sourceRoll?.maxProfitPercentage?.toString()}
                  onChange={(...props) =>
                    instrumentChangeHandler(
                      ins.instrumentId,
                      "sourceRoll",
                      ...props,
                    )
                  }
                />
              )}
              <div />
              <TextInput
                label="مرز DTM"
                name="dtmPoint"
                value={ins?.destinationRoll?.dtmPoint?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "destinationRoll",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل ٪ ITM در کمتر از مرز"
                name="minITMPercentage_OverDTM"
                value={ins?.destinationRoll?.minITMPercentage_OverDTM?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "destinationRoll",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل دلتا در بیشتر از مرز"
                name="minDeltaPercentage_UnderDTM"
                value={ins?.destinationRoll?.minDeltaPercentage_UnderDTM?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "destinationRoll",
                    ...props,
                  )
                }
              />
              <div />
              <TextInput
                label="حداقل ٪ بازده روزانه رول"
                name="minProfitPercentage"
                value={ins?.rollSpecification?.minProfitPercentage?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "rollSpecification",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل ٪ بازده روزانه خالص ارزش زمانی رول"
                name="minNetTimeValueDailyReturnPercentage"
                value={ins?.rollSpecification?.minNetTimeValueDailyReturnPercentage?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "rollSpecification",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل ٪ بهبود سر به سری"
                name="minBreakEvenImprovementPercentage"
                value={ins?.rollSpecification?.minBreakEvenImprovementPercentage?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "rollSpecification",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل بهبود دلتا"
                name="minDeltaImprovement"
                value={ins?.rollSpecification?.minDeltaImprovement?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "rollSpecification",
                    ...props,
                  )
                }
              />
            </>
          ))
        ) : (
          <></>
        )}
      </div>
      <div className="w-[100px] mr-auto">
        <Button onClick={setConfigHandler} text="ثبت" variant="contained" />
      </div>
    </div>
  );
};

export default SettingModal;
